import find from 'lodash.find';
import { matchPath } from 'react-router';
import {
  fork,
  put,
  race,
  select,
  take,
  takeEvery,
} from 'redux-saga/effects';
import { BOOTSTRAPPING_SUCCESS } from '../actions/user';
import {
  hydrateView,
  trackView,
  HYDRATE_VIEW,
  TRACK_VIEW,
} from '../actions/views';
import Routes from '../routes';
import { hydrateEmployeeProfileView } from '../scenes/company-admin/ambassadors/components/employee-profile-view/saga/hydrateEmployeeProfileView';
import { hydrateEmployeeEditionView } from '../scenes/company-admin/employee-edition/saga/hydrateEmployeeEditionView';
import { hydrateSignupView } from '../scenes/onboarding/signup/sagas/signup';
import { hydrateSoftDeletionModal } from '../scenes/user/account/sagas/soft-deletion';
import { hydrateEditProfileView } from '../scenes/user/profile/saga/hydrateEditProfileView';
import { hydrateDashboard } from '../scenes/user/dashboard/professionals/saga/dashboard';
import { userCredentialsSelector } from '../selectors';
import { getPathnameId } from '../utils/routes';
import {
  hydrateOneDayWithACeoView,
  hydrateProfessionalView,
  trackProfessionalView,
} from '../scenes/public/profile/sagas/hydrateProfessionalView';
import { trackHomeView } from './home';
import { hydrateAppointmentsView } from './appointments';
import {
  hydrateConversationView,
  hydrateDownloadConversationAppointmentIcalendarView,
} from './conversations';
import { hydrateConversationCreationView } from './conversation-creation';
import {
  hydrateSearchView,
  trackSearchView,
} from './search';
import { subscribe } from './realtime';
import { hydrateRedCarpetsView } from './red-carpets';
import {
  hydrateMentorModerationList,
  hydrateMentorModerationPageView,
  hydrateMentorModerationStandbyList,
  hydratePendingSchoolModerationList,
  hydratePendingSchoolModerationView,
} from './moderator';

export const views = {
  [Routes.home]: {
    track: trackHomeView,
  },
  [Routes.signupView]: {
    hydrate: hydrateSignupView,
  },
  [Routes.professional]: {
    hydrate: hydrateProfessionalView,
    track: trackProfessionalView,
    subscription: 'EmployeeChannel',
  },
  [Routes.oneDayWithACeo]: {
    hydrate: hydrateOneDayWithACeoView,
  },
  [Routes.conversationCreation]: {
    hydrate: hydrateConversationCreationView,
    subscription: 'EmployeeChannel',
    requiresAuthentication: true,
  },
  [Routes.conversation]: {
    hydrate: hydrateConversationView,
    initialized: false,
    requiresAuthentication: true,
  },
  [Routes.bookAppointment]: {
    hydrate: hydrateConversationView,
    initialized: false,
    requiresAuthentication: true,
  },
  [Routes.updateAppointment]: {
    hydrate: hydrateConversationView,
    initialized: false,
    requiresAuthentication: true,
  },
  [Routes.deleteAppointment]: {
    hydrate: hydrateConversationView,
    initialized: false,
    requiresAuthentication: true,
  },
  [Routes.claimAppointment]: {
    hydrate: hydrateConversationView,
    initialized: false,
    requiresAuthentication: true,
  },
  [Routes.downloadConversationAppointmentIcalendar]: {
    hydrate: hydrateDownloadConversationAppointmentIcalendarView,
    initialized: false,
    requiresAuthentication: true,
  },
  [Routes.conversations]: {},
  [Routes.appointments]: {
    hydrate: hydrateAppointmentsView,
    initialized: false,
    requiresAuthentication: true,
  },
  [Routes.search]: {
    hydrate: hydrateSearchView,
    initialized: false,
    track: trackSearchView,
  },
  [Routes.redCarpets]: {
    hydrate: hydrateRedCarpetsView,
    initialized: false,
    requiresAuthentication: true,
  },
  [Routes.profile]: {
    hydrate: hydrateEditProfileView,
    initialized: false,
    requiresAuthentication: true,
  },
  [Routes.profileAvatarEdit]: {
    hydrate: hydrateEditProfileView,
    initialized: false,
    requiresAuthentication: true,
  },
  [Routes.settingsSoftDeletion]: {
    hydrate: hydrateSoftDeletionModal,
  },
  [Routes.moderatorDashboard]: {
    hydrate: hydrateMentorModerationList,
    initialized: false,
    requiresAuthentication: true,
  },
  [Routes.pendingMentorModerationsList]: {
    hydrate: hydrateMentorModerationList,
    initialized: false,
    requiresAuthentication: true,
  },
  [Routes.standbyMentorModerationsList]: {
    hydrate: hydrateMentorModerationStandbyList,
    initialized: false,
    requiresAuthentication: true,
  },
  [Routes.mentorModerationView]: {
    hydrate: hydrateMentorModerationPageView,
    initialized: false,
    requiresAuthentication: true,
  },
  [Routes.mentorModerationViewStandby]: {
    hydrate: hydrateMentorModerationPageView,
    initialized: false,
    requiresAuthentication: true,
  },
  [Routes.mentorModerationViewReject]: {
    hydrate: hydrateMentorModerationPageView,
    initialized: false,
    requiresAuthentication: true,
  },
  [Routes.schoolModerationList]: {
    hydrate: hydratePendingSchoolModerationList,
    initialized: false,
    requiresAuthentication: true,
  },
  [Routes.schoolModerationView]: {
    hydrate: hydratePendingSchoolModerationView,
    initialized: false,
    requiresAuthentication: true,
  },
  [Routes.softDeletionGoodbye]: {
    hydrate: false,
    initialized: false,
  },
  [Routes.accountRestrictedPage]: {
    hydrate: false,
    initialized: false,
  },
  [Routes.reviewView]: {
    requiresAuthentication: true,
  },
  [Routes.dashboard]: {
    hydrate: hydrateDashboard,
    initialized: false,
    requiresAuthentication: true,
  },
  [Routes.companyAdminShowAmbassador]: {
    hydrate: hydrateEmployeeProfileView,
  },
  [Routes.companyAdminEditAmbassador]: {
    hydrate: hydrateEmployeeEditionView,
  },
  [Routes.mjgAdminCompaniesShowEmployee]: {
    hydrate: hydrateEmployeeProfileView,
  },
  [Routes.mjgAdminCompaniesEditEmployee]: {
    hydrate: hydrateEmployeeEditionView,
  },
};

export const getMatchingRoute = pathname => find(Routes, route => matchPath(pathname, {
  path: route,
  exact: true,
}));

export function* initializeViewSaga() {
  yield takeEvery('@@router/LOCATION_CHANGE', function* ({ payload }) {
    const pathname = payload?.location?.pathname;
    const matchingRoute = getMatchingRoute(pathname);
    if (matchingRoute) {
      if(views[matchingRoute]) {
        const id = getPathnameId(matchingRoute, pathname);
        // If we currently are on protected page
        // Display signin so the user can sign in to unlock the view
        if (views && views[matchingRoute] && views[matchingRoute].requiresAuthentication) {
          // We use only credentials selector because users not already loaded and not needed
          // And we only display signin if no credentials founds
          const loggedUser = yield select(userCredentialsSelector);
          if (!loggedUser) {
            const { abort } = yield race({
              success: take([ BOOTSTRAPPING_SUCCESS ]), // Prevent loading page before signin success
              abort: take('@@router/LOCATION_CHANGE'),
            });
            if (abort) {
              return;
            }
          }
        }
        yield put(hydrateView(matchingRoute, id));
        yield put(trackView({ route: matchingRoute, pathname, id }));
        if (views[matchingRoute].subscription) {
          yield fork(
            subscribe,
            views[matchingRoute].subscription,
            { closeOnLocationChange: true },
            { id }
          );
        }
      }
    }
  });
}

export function* hydrateViewSaga() {
  while (true) { // eslint-disable-line no-constant-condition
    const { route, id } = yield take(HYDRATE_VIEW);
    const view = views[route];

    if (view && view.hydrate) {
      yield fork(view.hydrate, id, view.initialized);

      if (!view.initialized) {
        view.initialized = true;
      }
    }
  }
}

export function* trackViewSaga() {
  while (true) { // eslint-disable-line no-constant-condition
    const { route, pathname } = yield take(TRACK_VIEW);
    const view = views[route];

    if (view.track) {
      yield fork(view.track, { pathname });
    }
  }
}
